import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { PriorityDto, PriorityStore } from '../../../stores/configuration/events-n-requests/priorities-store';
import { nameof } from '../../../utils/object';
import { OrderDefinition } from 'stores/dataStore';
import TailwindMultiSelect from '../../editors/tailwind-multiselect';

interface PriorityEditorProps {
  value: PriorityDto[];
  onChange?: (value: PriorityDto[]) => void;
  priorities?: PriorityStore;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  prioritiesReceived?: string[];
  reloadOnChange?: boolean;
  loadDataOnOpen?: boolean;
  onBlur?: (event: React.FocusEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
}

const TailwindMultiplePriorityEditor: React.FC<PriorityEditorProps> = ({
  value,
  onChange,
  priorities,
  placeholder,
  clearable = true,
  disabled = false,
  className,
  prioritiesReceived,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<PriorityDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getPrioritiesMethod = useCallback(async () => {
    if (options.length > 0) return;
    try {
      setIsLoading(true);
      let filter;
      const orderBy: OrderDefinition[] = [{ direction: 'Ascending', field: nameof<PriorityDto>('name'), useProfile: false }];

      if (prioritiesReceived?.length > 0) {
        filter = [{ id: { in: { type: 'guid', value: prioritiesReceived } } }];
      }

      const result = await priorities?.getAllAsync({ searchQuery: '', skip: 0, take: 100000, orderBy, filter }, prioritiesReceived);

      if (result) {
        const mappedOptions = result.items;
        setOptions(mappedOptions);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [options.length, priorities, prioritiesReceived]);

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getPrioritiesMethod();
    }
  }, [loadDataOnOpen, options.length, getPrioritiesMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getPrioritiesMethod();
    }
  }, [loadDataOnOpen, getPrioritiesMethod, prioritiesReceived]);

  return (
    <TailwindMultiSelect
      options={
        isLoading
          ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
          : options.length > 0
          ? options.map(priority => ({
              id: priority.id,
              text: priority.name
            }))
          : [{ id: 'no-options', text: t('No options available'), disabled: true }]
      }
      value={value?.map(v => ({
        id: v.id,
        text: v.name
      }))}
      onChange={newValue => {
        const selectedPriorities = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
        onChange(selectedPriorities);
      }}
      placeholder={placeholder || t('Select priorities')}
      clearable={clearable}
      className={className}
      onBlur={onBlur}
      onFocus={onFocus}
      searchable={!isLoading}
      disabled={disabled}
      onOpenDropDown={onOpenDropDown}
    />
  );
};

export default connect(['priorities', PriorityStore])(TailwindMultiplePriorityEditor);
